import { useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { founderDashboardAPI } from '../../../../services/api';
import {
  Modal,
  PageHeader,
  PageState,
  ReasonList,
  StaleNotice,
  StatCard,
  StatusBadge,
} from './OutreachShared';
import { focusRing, humanize, useOutreachResource } from './outreachUtils';

function pick(source, keys, fallback = null) {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return fallback;
}

function decisionId() {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `daily-cap-${Date.now()}`;
}

export default function OutreachOverviewPage() {
  const resource = useOutreachResource(() => founderDashboardAPI.getOutreachSummary(), []);
  const summary = resource.data || {};
  const program = summary.program || {};
  const campaignCounts = summary.campaigns || {};
  const queue = summary.queue || {};
  const prerequisiteStatus = summary.prerequisites || {};
  const outcomes = summary.outcomes || summary.primaryOutcomes || {};
  const prerequisites =
    prerequisiteStatus.prerequisiteFailures ||
    summary.prerequisiteFailures ||
    summary.prerequisite_failures ||
    [];
  const pauseReason =
    summary.automaticPauseReason || summary.automatic_pause_reason || summary.pauseReason;
  const globalEnabled = program.mode === 'active' && program.safeMode === false;
  const dailyCap = pick(program, ['dailyCap', 'daily_cap'], 100);
  const dailyCapMaximum = pick(
    program,
    ['dailyCapMaximum', 'daily_cap_maximum'],
    25_000
  );
  const sentToday = pick(summary, ['sentToday', 'sent_today'], null);
  const treatment = pick(summary, ['treatmentCount', 'treatment_count'], 0);
  const holdout = pick(summary, ['holdoutCount', 'holdout_count'], 0);
  const holdoutPercent = pick(
    prerequisiteStatus,
    ['holdoutPercent', 'holdout_percent'],
    10
  );
  const providerHealth =
    prerequisiteStatus.lifecycleAsmConfigured && prerequisiteStatus.senderConfigured
      ? 'healthy'
      : 'blocked';
  const generatedAt = summary.generatedAt || summary.generated_at || summary.asOf;
  const [capDraft, setCapDraft] = useState('');
  const [capAcknowledged, setCapAcknowledged] = useState(false);
  const [capEditorOpen, setCapEditorOpen] = useState(false);
  const [savingCap, setSavingCap] = useState(false);
  const [mutationError, setMutationError] = useState(null);
  const [success, setSuccess] = useState(null);
  const parsedCap = Number(capDraft);
  const capIsValid =
    Number.isInteger(parsedCap) &&
    parsedCap >= 1 &&
    parsedCap <= dailyCapMaximum;
  const capIsIncrease = capIsValid && parsedCap > dailyCap;

  function openCapEditor() {
    setMutationError(null);
    setSuccess(null);
    setCapDraft(String(dailyCap));
    setCapAcknowledged(false);
    setCapEditorOpen(true);
  }

  async function saveDailyCap() {
    if (!capIsValid) return;
    setSavingCap(true);
    setMutationError(null);
    try {
      await founderDashboardAPI.updateOutreachProgram({
        dailyCap: parsedCap,
        confirmationVersion: program.confirmationVersion,
        ...(capIsIncrease
          ? {
              decision: {
                id: decisionId(),
                summary: `Approved raising the daily lifecycle cap from ${dailyCap} to ${parsedCap}.`,
              },
            }
          : {}),
      });
      setSuccess(
        `Daily lifecycle cap changed from ${dailyCap.toLocaleString()} to ${parsedCap.toLocaleString()}.`
      );
      setCapEditorOpen(false);
      await resource.reload();
    } catch (error) {
      setMutationError(
        error?.response?.data?.error?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'The daily lifecycle cap was not changed.'
      );
    } finally {
      setSavingCap(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Outreach overview"
        description="The safe operating picture for lifecycle and necessary status email."
        refreshing={resource.refreshing}
        onRefresh={resource.reload}
      />
      <StaleNotice generatedAt={generatedAt} loadedAt={resource.loadedAt} />

      {success ? (
        <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {success}
        </div>
      ) : null}
      {mutationError && !capEditorOpen ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {mutationError}
        </div>
      ) : null}

      <PageState
        loading={resource.loading}
        error={resource.error}
        permissionDenied={resource.permissionDenied}
        onRetry={resource.reload}
      >
        <section
          className={`rounded-xl border p-5 ${
            globalEnabled
              ? 'border-green-200 bg-green-50'
              : 'border-gray-300 bg-gray-100'
          }`}
          aria-label="Global lifecycle sending status"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3">
              {globalEnabled ? (
                <CheckCircleIcon className="h-7 w-7 flex-none text-green-700" />
              ) : (
                <PauseCircleIcon className="h-7 w-7 flex-none text-gray-600" />
              )}
              <div>
                <p className="font-semibold text-gray-900">
                  Global kill switch: {globalEnabled ? 'sending permitted' : 'sending stopped'}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Program {humanize(program.mode || 'off')} · safe mode{' '}
                  {program.safeMode === false ? 'off' : 'on'}. This authoritative server status
                  cannot be bypassed by campaign controls.
                </p>
              </div>
            </div>
            <StatusBadge value={globalEnabled ? 'on' : 'off'} />
          </div>
        </section>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <section
            className={`rounded-xl border p-4 shadow-sm ${
              sentToday !== null && sentToday >= dailyCap
                ? 'border-amber-200 bg-amber-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Daily lifecycle cap
                </p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {dailyCap.toLocaleString()}/day
                </p>
              </div>
              <button
                type="button"
                onClick={openCapEditor}
                className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 ${focusRing}`}
              >
                <PencilSquareIcon className="h-4 w-4" />
                Change
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-600">
              {sentToday === null
                ? `Owner adjustable up to 25,000/day`
                : `${sentToday.toLocaleString()} counted today`}
            </p>
          </section>
          <StatCard
            label="Normal schedule"
            value={treatment}
            detail="Actual lifecycle candidates assigned without a comparison delay"
          />
          <StatCard
            label="7-day comparison delay"
            value={holdout}
            detail={`${holdoutPercent}% are assigned persistently; this card shows the actual count`}
          />
          <StatCard
            label="Provider health"
            value={humanize(providerHealth)}
            detail="SendGrid and lifecycle suppression checks"
            tone={String(providerHealth).toLowerCase() === 'healthy' ? 'positive' : 'warning'}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
              <h4 className="font-semibold text-gray-900">Launch prerequisites</h4>
            </div>
            <div className="mt-3">
              <ReasonList reasons={prerequisites} emptyLabel="All reported prerequisites pass" />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
              <h4 className="font-semibold text-gray-900">Automatic pause</h4>
            </div>
            <p className={`mt-3 text-sm ${pauseReason ? 'text-amber-800' : 'text-gray-600'}`}>
              {pauseReason ? humanize(pauseReason) : 'No automatic pause is currently reported.'}
            </p>
          </section>
        </div>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h4 className="font-semibold text-gray-900">Program activity & primary outcomes</h4>
          <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <div className="rounded-lg bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">Campaigns active</dt>
              <dd className="mt-1 text-lg font-bold text-gray-900">{campaignCounts.active ?? 0}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">Campaigns shadow</dt>
              <dd className="mt-1 text-lg font-bold text-gray-900">{campaignCounts.shadow ?? 0}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">Queue pending</dt>
              <dd className="mt-1 text-lg font-bold text-gray-900">{queue.pending ?? 0}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">Unknown evidence</dt>
              <dd className="mt-1 text-lg font-bold text-gray-900">{queue.unknown ?? 0}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">Suppressions</dt>
              <dd className="mt-1 text-lg font-bold text-gray-900">{summary.suppressions ?? 0}</dd>
            </div>
          </dl>
          {Object.keys(outcomes).length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No outcome evidence is available yet.</p>
          ) : (
            <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {Object.entries(outcomes).map(([key, value]) => (
                <div key={key} className="rounded-lg bg-gray-50 p-4">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {humanize(key)}
                  </dt>
                  <dd className="mt-1 text-xl font-bold text-gray-900">
                    {typeof value === 'object'
                      ? value.value ?? value.count ?? '—'
                      : value ?? '—'}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      </PageState>

      {capEditorOpen ? (
        <Modal
          title="Change daily lifecycle cap?"
          description="This changes only the maximum number of optional lifecycle messages allocated per Chicago calendar day. It does not enable sending, campaigns, or jobs."
          confirmLabel="Save daily cap"
          confirmDisabled={
            !capIsValid ||
            parsedCap === dailyCap ||
            (capIsIncrease && !capAcknowledged)
          }
          busy={savingCap}
          onConfirm={saveDailyCap}
          onClose={() => !savingCap && setCapEditorOpen(false)}
        >
          {mutationError ? (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {mutationError}
            </div>
          ) : null}
          <label className="block text-sm font-semibold text-gray-800">
            Messages per day
            <input
              type="number"
              min="1"
              max={dailyCapMaximum}
              step="1"
              inputMode="numeric"
              value={capDraft}
              onChange={(event) => {
                setCapDraft(event.target.value);
                setCapAcknowledged(false);
                setMutationError(null);
              }}
              className={`mt-2 min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 text-base text-gray-900 ${focusRing}`}
            />
          </label>
          <p className="mt-2 text-xs text-gray-600">
            Choose a whole number from 1 through 25,000. Current cap:{' '}
            {dailyCap.toLocaleString()}.
          </p>
          {capDraft && !capIsValid ? (
            <p role="alert" className="mt-2 text-sm font-medium text-red-700">
              Enter a whole number from 1 through 25,000.
            </p>
          ) : null}
          {capIsIncrease ? (
            <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              <input
                type="checkbox"
                checked={capAcknowledged}
                onChange={(event) => setCapAcknowledged(event.target.checked)}
                className="mt-0.5 h-5 w-5 flex-none"
              />
              <span>
                I understand this raises the maximum possible lifecycle sends
                from {dailyCap.toLocaleString()} to {parsedCap.toLocaleString()}{' '}
                per day. The change will be audited.
              </span>
            </label>
          ) : capIsValid && parsedCap < dailyCap ? (
            <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
              A decrease takes effect immediately and may stop additional sends
              for the rest of the current day.
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
